import { useTranslation } from '@pancakeswap/localization'
import { ApprovalState } from 'hooks/useApproveCallback'
import { Field } from 'state/mint/actions'
import { styled } from 'styled-components'
import { ethereumTokens } from '@pancakeswap/tokens'
import { Link, RowBetween, Message, MessageText, Button, Dots } from '@pancakeswap/uikit'
import { Currency, CurrencyAmount } from '@pancakeswap/swap-sdk-core'
import { SendTransactionResult } from 'wagmi/actions'
import { useCallback, useMemo, useState } from 'react'

const InlineLink = styled(Link)`
  display: inline-flex;
  text-decoration: underline;
  color: #d67e0a;
`

interface ApproveLiquidityTokensProps {
  currencies: {
    [Field.CURRENCY_A]?: Currency
    [Field.CURRENCY_B]?: Currency
  }
  shouldShowApprovalGroup: boolean
  showFieldAApproval: boolean
  approveACallback: () => Promise<SendTransactionResult>
  revokeACallback: () => Promise<SendTransactionResult>
  currentAllowanceA: CurrencyAmount<Currency> | undefined
  approvalA: ApprovalState
  showFieldBApproval: boolean
  approveBCallback: () => Promise<SendTransactionResult>
  revokeBCallback: () => Promise<SendTransactionResult>
  currentAllowanceB: CurrencyAmount<Currency> | undefined
  approvalB: ApprovalState
}

export default function ApproveLiquidityTokens({
  shouldShowApprovalGroup,
  showFieldAApproval,
  approvalA,
  approveACallback,
  revokeACallback,
  currentAllowanceA,
  currencies,
  showFieldBApproval,
  approvalB,
  approveBCallback,
  revokeBCallback,
  currentAllowanceB,
}: ApproveLiquidityTokensProps) {
  const { t } = useTranslation()
  const [isAttempting, setAttempting] = useState<string>()

  const revokeANeeded = useMemo(() => {
    return (
      showFieldAApproval &&
      currentAllowanceA?.greaterThan(0) &&
      currencies[Field.CURRENCY_A]?.chainId === ethereumTokens.usdt.chainId &&
      currencies[Field.CURRENCY_A]?.wrapped.address.toLowerCase() === ethereumTokens.usdt.address.toLowerCase()
    )
  }, [showFieldAApproval, currentAllowanceA, currencies])
  const revokeBNeeded = useMemo(() => {
    return (
      showFieldBApproval &&
      currentAllowanceB?.greaterThan(0) &&
      currencies[Field.CURRENCY_B]?.chainId === ethereumTokens.usdt.chainId &&
      currencies[Field.CURRENCY_B]?.wrapped.address.toLowerCase() === ethereumTokens.usdt.address.toLowerCase()
    )
  }, [showFieldBApproval, currentAllowanceB, currencies])
  const anyRevokeNeeded = revokeANeeded || revokeBNeeded

  const handleApproveA = useCallback(() => {
    setAttempting('A')
    approveACallback().finally(() => setAttempting(undefined))
  }, [approveACallback])

  const handleApproveB = useCallback(() => {
    setAttempting('B')
    approveBCallback().finally(() => setAttempting(undefined))
  }, [approveBCallback])

  return shouldShowApprovalGroup ? (
    <RowBetween style={{ gap: '8px' }}>
      {anyRevokeNeeded && (
        <Message variant="warning">
          <MessageText>
            <span>
              {t('USDT on Ethereum requires resetting approval when spending allowances are too low.')}
              <InlineLink
                external
                fontSize={14}
                href="https://docs.pre-prod.v3-cbr.pages.dev/products/pancakeswap-exchange/faq#why-do-i-need-to-reset-approval-on-usdt-before-enabling-approving"
              >
                {' '}
                {t('Learn More')}
                {' >>'}
              </InlineLink>
            </span>
          </MessageText>
        </Message>
      )}
      {showFieldAApproval &&
        (revokeANeeded ? (
          <Button onClick={revokeACallback} disabled={isAttempting === 'A' || approvalA === ApprovalState.PENDING} width="100%">
            {isAttempting === 'A' || approvalA === ApprovalState.PENDING ? (
              <Dots>{t('Reset Approval on USDT', { asset: currencies[Field.CURRENCY_A]?.symbol })}</Dots>
            ) : (
              t('Reset Approval on USDT', { asset: currencies[Field.CURRENCY_A]?.symbol })
            )}
          </Button>
        ) : (
          <Button onClick={handleApproveA} disabled={isAttempting === 'A' || approvalA === ApprovalState.PENDING} width="100%">
            {isAttempting === 'A' || approvalA === ApprovalState.PENDING ? (
              <Dots>{t('Enabling %asset%', { asset: currencies[Field.CURRENCY_A]?.symbol })}</Dots>
            ) : (
              t('Enable %asset%', { asset: currencies[Field.CURRENCY_A]?.symbol })
            )}
          </Button>
        ))}
      {showFieldBApproval &&
        (revokeBNeeded ? (
          <Button onClick={revokeBCallback} disabled={isAttempting === 'B' || approvalB === ApprovalState.PENDING} width="100%">
            {isAttempting === 'B' || approvalB === ApprovalState.PENDING ? (
              <Dots>{t('Reset Approval on USDT', { asset: currencies[Field.CURRENCY_B]?.symbol })}</Dots>
            ) : (
              t('Reset Approval on USDT', { asset: currencies[Field.CURRENCY_B]?.symbol })
            )}
          </Button>
        ) : (
          <Button onClick={handleApproveB} disabled={isAttempting === 'B' || approvalB === ApprovalState.PENDING} width="100%">
            {isAttempting === 'B' || approvalB === ApprovalState.PENDING ? (
              <Dots>{t('Enabling %asset%', { asset: currencies[Field.CURRENCY_B]?.symbol })}</Dots>
            ) : (
              t('Enable %asset%', { asset: currencies[Field.CURRENCY_B]?.symbol })
            )}
          </Button>
        ))}
    </RowBetween>
  ) : null
}
